// src/components/common/buttons.ts

import { createElement } from "../../utils/domHelpers";
import { ButtonFooterOptions } from "../../types/components";

/**
 * Creates a generic button or button-styled link with customizable properties.
 *
 * @param options - Configuration options for the button
 * @returns The created button or anchor HTMLElement
 */
export function createButton(options: {
	id?: string;
	text: string;
	style?:
		| "primary"
		| "secondary"
		| "success"
		| "danger"
		| "warning"
		| "info"
		| "light"
		| "dark"
		| "link";
	size?: "default" | "sm" | "lg";
	fullWidth?: boolean;
	onClick?: (event: MouseEvent) => void;
	disabled?: boolean;
	additionalClasses?: string;
	type?: "button" | "submit" | "reset";
	parent?: HTMLElement;
	href?: string; // Link URL if this should be an anchor
	target?: "_blank" | "_self"; // Link target
	rel?: string; // Link rel attribute
}): HTMLButtonElement | HTMLAnchorElement {
	const {
		id,
		text,
		style = "primary",
		size = "default",
		fullWidth = false,
		onClick,
		disabled = false,
		additionalClasses = "",
		type = "button",
		parent,
		href,
		target,
		rel,
	} = options;

	// Build the class name based on options
	let className = `btn btn-${style}`;

	// Add size class if not default
	if (size === "sm") className += " btn-sm";
	if (size === "lg") className += " btn-lg";

	// Add full width class if needed
	if (fullWidth) className += " w-100";

	// Add any additional classes
	if (additionalClasses) className += ` ${additionalClasses}`;

	let element: HTMLButtonElement | HTMLAnchorElement;

	// Create either a button or an anchor element
	if (href) {
		// Create an anchor styled as a button
		element = createElement("a", {
			id: id,
			className: className,
			innerHTML: text,
			attributes: {
				href: disabled ? "#" : href,
				role: "button",
			},
		}) as HTMLAnchorElement;

		// Add target and rel if provided
		if (target && !disabled) {
			element.setAttribute("target", target);

			// Add rel="noopener noreferrer" for security when using _blank
			if (target === "_blank") {
				element.setAttribute("rel", rel || "noopener noreferrer");
			} else if (rel) {
				element.setAttribute("rel", rel);
			}
		}

		// For anchor elements, we simulate disabled state
		if (disabled) {
			element.classList.add("disabled");
			element.setAttribute("aria-disabled", "true");
			element.addEventListener("click", (e) => {
				e.preventDefault(); // Prevent navigation
			});
		}
	} else {
		// Create a regular button
		element = createElement("button", {
			id: id,
			className: className,
			innerHTML: text,
			attributes: { type },
		}) as HTMLButtonElement;

		// Set disabled state for button
		element.disabled = disabled;
	}

	// Add opacity class if disabled (for visual consistency with existing code)
	if (disabled) {
		element.classList.add("opacity-25");
	}

	// Add click handler if provided
	if (onClick) {
		element.addEventListener("click", onClick as EventListener);
	}

	// Append to parent if provided
	if (parent) {
		parent.appendChild(element);
	}

	return element;
}

/**
 * Creates the fixed footer container used for primary action buttons (Next, Submit, Pay).
 * Appends a placeholder div for the actual button(s).
 *
 * @param parent - The parent element to append the footer to (usually the form or main content div).
 * @param options - Configuration options for the button within the footer.
 * @returns The created footer HTMLElement.
 */
export function createFooterButton(
	parent: HTMLElement,
	options: ButtonFooterOptions = {}
): HTMLElement {
	// Remove existing footer button container first
	const existingFooter = document.getElementById("button-parent");
	existingFooter?.remove();

	const {
		buttonId = "submitButton",
		buttonText = "Next",
		buttonClass = "btn btn-primary w-100",
		initialOpacity = 0.25, // Default to semi-transparent (disabled look)
		disabled = true, // Default to disabled
	} = options;

	const buttonParent = createElement("div", {
		id: "button-parent",
		className:
			"container-fluid bg-white shadow-lg rounded-top position-fixed bottom-0 start-0 p-3 z-3 text-center", // Classes from original
	});

	const buttonContainer = createElement(
		"div",
		{
			id: "button-container", // ID from original
			className: "d-flex justify-content-around w-100",
		},
		buttonParent
	);

	// Create the main action button
	const submitButton = createElement("button", {
		id: buttonId,
		className: `${buttonClass} ${
			disabled ? `opacity-${initialOpacity * 100}` : ""
		}`, // Apply initial opacity if disabled
		innerHTML: buttonText,
		attributes: { type: "submit" }, // Default to type="submit"
	});
	submitButton.disabled = disabled; // Set initial disabled state

	buttonContainer.appendChild(submitButton); // Add the button

	parent.appendChild(buttonParent); // Append the footer structure

	// Add event listener to adjust body padding when footer is added/removed (or on resize)
	// This might be better handled globally on window load/resize
	// adjustBodyPaddingForFooter(); // Call helper from domUtils

	return buttonParent; // Return the footer container
}

export function addButtonFooter(): HTMLElement {
	const buttonParent = document.createElement("div");
	// Use bg-body-tertiary for better dark-theme compatibility
	buttonParent.className =
		"container-fluid bg-black shadow-lg rounded-top position-fixed bottom-0 start-0 p-3 z-3 text-center";
	buttonParent.id = "button-parent";

	const buttonContainer = document.createElement("div");
	buttonContainer.className = "d-flex flex-column justify-content-around w-100";
	buttonContainer.id = "button-container";
	buttonParent.appendChild(buttonContainer);

	return buttonParent;
}
